"use client";

/**
 * Client body for /use-cases/support — the support & email demo page. Copy in
 * the `ucSupport` namespace (NL lead, EN lockstep). The full demo video is
 * embedded through <VideoBlock>, which renders an honest "being finished"
 * note until the final deliverable is published in the video manifest — never
 * an old cut or a placeholder.
 */

import {
  FileSearch,
  FolderKanban,
  Inbox,
  Mail,
  MailCheck,
  PenLine,
  ShieldCheck,
  UserCheck,
} from "lucide-react";
import { MarketingShell } from "@/components/marketing/marketing-shell";
import {
  Faq,
  UseCaseHero,
  Section,
  SectionHeader,
  WorkflowSteps,
  SecurityNote,
  RelatedLinks,
  CtaBanner,
} from "@/components/marketing/marketing-kit";
import { VideoBlock } from "@/components/marketing/video/video-block";
import { useT } from "@/i18n";

export function SupportContent() {
  const t = useT();
  const k = (s: string) => `ucSupport.${s}`;

  const steps = [
    { key: "s1", icon: <Inbox size={18} /> },
    { key: "s2", icon: <FolderKanban size={18} /> },
    { key: "s3", icon: <FileSearch size={18} /> },
    { key: "s4", icon: <PenLine size={18} /> },
    { key: "s5", icon: <ShieldCheck size={18} /> },
    { key: "s6", icon: <UserCheck size={18} /> },
    { key: "s7", icon: <MailCheck size={18} /> },
  ].map((s) => ({
    icon: s.icon,
    tag: t(k(`how.steps.${s.key}.tag`)),
    title: t(k(`how.steps.${s.key}.title`)),
    body: t(k(`how.steps.${s.key}.body`)),
  }));

  return (
    <MarketingShell>
      <UseCaseHero
        eyebrow={t(k("hero.eyebrow"))}
        title={t(k("hero.title"))}
        highlight={t(k("hero.highlight"))}
        subtitle={t(k("hero.subtitle"))}
        primary={{ href: "/demo", label: t(k("hero.primaryCta")) }}
        secondary={{ href: "#video", label: t(k("hero.secondaryCta")) }}
        bullets={[t(k("hero.bullet1")), t(k("hero.bullet2")), t(k("hero.bullet3"))]}
      />

      <Section id="video">
        <SectionHeader
          eyebrow={t(k("video.eyebrow"))}
          title={t(k("video.title"))}
          description={t(k("video.description"))}
        />
        <div className="mt-10">
          <VideoBlock id="fixcontrol-support-nl" posterCaption={t(k("video.title"))} />
        </div>
      </Section>

      <Section muted>
        <SectionHeader
          eyebrow={t(k("how.eyebrow"))}
          title={t(k("how.title"))}
          description={t(k("how.description"))}
        />
        <WorkflowSteps steps={steps} />
      </Section>

      <Section>
        <SectionHeader eyebrow={t(k("stay.eyebrow"))} title={t(k("stay.title"))} />
        <SecurityNote icon={<Mail size={18} />} title={t(k("stay.title"))}>
          {t(k("stay.body"))}
        </SecurityNote>
      </Section>

      <Section muted>
        <SectionHeader eyebrow={t(k("faq.eyebrow"))} title={t(k("faq.title"))} />
        <Faq
          items={["q1", "q2", "q3", "q4"].map((q) => ({
            q: t(k(`faq.${q}.q`)),
            a: t(k(`faq.${q}.a`)),
          }))}
        />
      </Section>

      <Section>
        <RelatedLinks
          title={t("mk.relatedTitle")}
          links={[
            {
              href: "/use-cases/engineering",
              label: t(k("related.engineering.label")),
              description: t(k("related.engineering.description")),
            },
            {
              href: "/use-cases/freshdesk-jira-approved-replies",
              label: t(k("related.approvedReplies.label")),
              description: t(k("related.approvedReplies.description")),
            },
            {
              href: "/product",
              label: t(k("related.product.label")),
              description: t(k("related.product.description")),
            },
          ]}
        />
      </Section>

      <CtaBanner
        title={t(k("cta.title"))}
        description={t(k("cta.description"))}
        primary={{ href: "/demo", label: t(k("cta.primary")) }}
        secondary={{ href: "/contact", label: t(k("cta.secondary")) }}
      />
    </MarketingShell>
  );
}
